/**
 * Opens the connect flow in a centered popup instead of navigating the tab
 * away. After authorizing, the popup closes itself (see ConnectPopupBridge).
 * A real window (not an iframe) is required — providers send X-Frame-Options: DENY.
 */

/** window.open names — let the bridge recognize our popup if the provider
 * drops window.opener (COOP). Must match the bridge. */
export const CONNECT_POPUP_NAMES = [
  "github-connect",
  "slack-connect",
  "gmail-connect",
  "calendar-connect",
  "google-drive-connect",
  "jira-connect",
  "linear-connect",
  "figma-connect",
  "dropbox-connect",
  "zoom-connect",
  "asana-connect",
  "trello-connect",
  "box-connect",
  "clickup-connect",
  "pagerduty-connect",
  "notion-connect",
  "gitlab-connect",
  "confluence-connect",
  "onedrive-connect",
  "sentry-connect",
  "salesforce-connect",
  "teams-connect",
  "outlook-connect",
  "intercom-connect",
] as const;

/** Open `url` in a centered popup. Returns null if the browser blocked it. */
export function openCenteredPopup(
  url: string,
  name = "connect",
  width = 700,
  height = 620,
): Window | null {
  if (typeof window === "undefined") return null;

  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
  const winW = window.outerWidth || window.innerWidth || width;
  const winH = window.outerHeight || window.innerHeight || height;

  // Integers only — fractional/NaN values make Chromium open a tab instead.
  const w = Math.round(width);
  const h = Math.round(height);
  const left = Math.round(dualScreenLeft + Math.max(0, (winW - w) / 2));
  const top = Math.round(dualScreenTop + Math.max(0, (winH - h) / 2));

  // popup=1 asks Chromium for a floating window rather than a tab.
  const popup = window.open(
    url,
    name,
    `popup=1,scrollbars=1,resizable=1,width=${w},height=${h},top=${top},left=${left}`,
  );
  popup?.focus();
  return popup ?? null;
}

/**
 * Active workspace id, read the same way the settings gallery reads it.
 * Nearly every connect route is workspace-scoped.
 */
export function readWorkspaceId(): string {
  if (typeof window === "undefined") return "";
  try {
    const cached = window.localStorage.getItem("cached_workspace");
    if (cached) {
      const ws = JSON.parse(cached);
      const id = ws?._id || ws?.id;
      if (id) return String(id);
    }
  } catch {
    /* ignore */
  }
  return (
    localStorage.getItem("workspaceId") ||
    sessionStorage.getItem("currentWorkspaceId") ||
    ""
  );
}

/**
 * Run a connector's OAuth flow from the `connectRoute` the unfurl endpoint
 * hands back, so callers never need a per-connector switch. Resolves once the
 * popup closes — the caller decides what to refetch.
 */
export function connectViaRoute(connectorId: string, connectRoute: string): Promise<void> {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3002";
  const workspaceId = readWorkspaceId();
  const query = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : "";
  const url = `${apiUrl}${connectRoute}${query}`;

  const name = `${connectorId}-connect`;
  const popup = openCenteredPopup(url, name);
  if (!popup) {
    // Blocked — a full-page redirect still completes the flow.
    window.location.href = url;
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const poll = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(poll);
        resolve();
      }
    }, 600);
  });
}
